import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography, Switch, IconButton, Snackbar, Alert, Tooltip, Grid, Card, CardHeader, CardContent, Divider, ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { Add } from '@mui/icons-material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import SimCardIcon from '@mui/icons-material/SimCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { DataTableWithStaticSchema } from 'components/DataTable/DataTableWithStaticSchema';
import { Logger } from 'utils/Logger/Logger';
import { apiClient } from 'utils/ApiClient';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { useModulePermissions } from 'hooks/useModulePermissions';

const queueCountOf = (row: any): number => {
  if (typeof row?.messagingQueueCount === 'number') return row.messagingQueueCount;
  return Array.isArray(row?.messagingQueue) ? row.messagingQueue.length : 0;
};

const schemaObject = {
  obj: {
    poolName: { type: String },
    location: { type: String },
    countryCode: { type: String },
    poolType: { type: String },
    status: { type: String },
    messagingQueue: { type: Array },
    workers: { type: Array },
  }
};

export const WorkerPool: React.FC = () => {
  const user = useSelector((state: RootState) => state.authentication.user);
  const { isUserSuperAdmin } = useModulePermissions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ _id: '', poolName: '', location: '', countryCode: '', poolType: 'GSM' });
  const [refresh, setRefresh] = useState(0);
  const [creating, setCreating] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [cannotDeleteOpen, setCannotDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [refreshingSecret, setRefreshingSecret] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [viewMode, setViewMode] = useState<'table' | 'tiles'>('table');
  const [pools, setPools] = useState<any[]>([]);
  const [loadingTiles, setLoadingTiles] = useState<boolean>(false);

  const emailDomain = (user?.email || '').split('@')[1]?.toLowerCase();
  const domainAllowed = emailDomain === 'sharadtech.com' || emailDomain === 'scenicmediahub.com';
  if (!isUserSuperAdmin || !domainAllowed) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Access Denied</Typography>
        <Typography variant="body1" color="textSecondary">You don't have permission to access Worker Pool.</Typography>
      </Box>
    );
  }

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const columnsToExclude: string[] = ['_id', '__v', 'createdAt', 'updatedAt'];
  const columnsWithDisabledFilter: string[] = ['messagingQueue', 'workers', 'actions'];

  const columnRenderers = useMemo(() => ({
    status: (row: any) => (
      <Switch
        checked={row.status === 'active'}
        onChange={() => toggleStatus(row)}
        color={row.status === 'active' ? 'success' : 'warning'}
      />
    ),
    poolType: (row: any) => (
      <Chip
        label={row.poolType || '-'}
        size="small"
        variant="filled"
        sx={{
          bgcolor: row.poolType === 'WhatsApp' ? 'success.main' : '#7b1fa2',
          color: 'white'
        }}
      />
    ),
    messagingQueue: (row: any) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          size="small" 
          variant="contained" 
          color="secondary" 
          startIcon={<VisibilityIcon fontSize="small" />} 
          onClick={() => {
            try {
              window.location.hash = `/administration/worker-pool/${row._id}/messaging-queue`;
            } catch (e) {
              Logger.error('Settings/WorkerPool', 'Failed to navigate to messaging queue page: ' + (e as any)?.message);
            }
          }}
          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
        >
          Queue
        </Button>
        <Button 
          size="small" 
          color="error" 
          variant="contained" 
          startIcon={<CleaningServicesIcon fontSize="small" />} 
          onClick={() => { setSelectedRow(row); setPurgeConfirmOpen(true); }}
          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
        >
          Purge
        </Button>
      </Box>
    ),
    workers: (row: any) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          size="small" 
          variant="contained" 
          color="secondary" 
          startIcon={<SimCardIcon fontSize="small" />} 
          onClick={() => {
            try {
              window.location.hash = `/administration/worker-pool/${row._id}/workers`;
            } catch (e) {
              Logger.error('Settings/WorkerPool', 'Failed to navigate to workers page: ' + (e as any)?.message);
            }
          }}
          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
        >
          Workers
        </Button>
      </Box>
    ),
    actions: (row: any) => (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Edit">
          <Button 
            aria-label="edit" 
            color="secondary" 
            variant="contained" 
            size="small" 
            onClick={() => onEdit(row)}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <EditIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Delete">
          <Button 
            aria-label="delete" 
            color="error" 
            variant="contained" 
            size="small" 
            onClick={() => onDelete(row)}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <DeleteIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Credentials">
          <Button 
            aria-label="credentials" 
            color="info" 
            variant="contained" 
            size="small" 
            onClick={() => openCredentials(row)}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <KeyIcon fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
    ),
  }), []);

  const openCredentials = (row: any) => {
    setSelectedRow(row);
    setCredDialogOpen(true);
  };

  const copyToClipboard = async (value: string) => {
    try { await navigator.clipboard.writeText(value); } catch {}
  };

  const handleRefreshSecret = async () => {
    if (!selectedRow?._id) return;
    try {
      setRefreshingSecret(true);
      const updated = await apiClient.post(`/iu/workerpool/${selectedRow._id}/secret/refresh`, {});
      const data = (updated as any).data || updated;
      setSelectedRow(data);
      setRefresh(x => x + 1);
    } catch (err: any) {
      Logger.error('Settings/WorkerPool', 'Failed to refresh secret: ' + (err?.message || ''));
    } finally {
      setRefreshingSecret(false);
    }
  };

  const onEdit = (row: any) => { setForm({ _id: row._id, poolName: row.poolName, location: row.location, countryCode: row.countryCode || '', poolType: row.poolType || 'GSM' }); setDialogOpen(true); };
  const onDelete = (row: any) => {
    const workersCount = Array.isArray(row.workers) ? row.workers.length : 0;
    const queueCount = queueCountOf(row);
    setSelectedRow(row);
    if (workersCount > 0 || queueCount > 0) {
      setCannotDeleteOpen(true);
    } else {
      setConfirmDeleteOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow?._id) return;
    try {
      await apiClient.delete(`/iu/workerpool/${selectedRow._id}`);
      setConfirmDeleteOpen(false);
      setSelectedRow(null);
      setRefresh(x => x + 1);
    } catch (err: any) {
      Logger.error('Settings/WorkerPool', 'Failed to delete worker pool: ' + (err?.message || ''));
    }
  };

  // Tiles view loads pools itself. Table view uses DataTableWithStaticSchema, so skip this
  // request there — otherwise the page downloads the list twice.
  useEffect(() => {
    if (viewMode !== 'tiles') return;
    const load = async () => {
      try {
        setLoadingTiles(true);
        const res = await apiClient.get('/iu/workerpool');
        const data = (res as any)?.data || res || [];
        setPools(Array.isArray(data) ? data : []);
      } catch (e: any) {
        Logger.error('Settings/WorkerPool', 'Failed to load pools for tiles: ' + (e?.message || ''));
        setPools([]);
      } finally {
        setLoadingTiles(false);
      }
    };
    load();
  }, [refresh, viewMode]);

  const handleConfirmPurge = async () => {
    if (!selectedRow?._id) return;
    try {
      setPurging(true);
      await apiClient.post(`/iu/workerpool/${selectedRow._id}/messaging-queue/purge`, {});
      setPurgeConfirmOpen(false);
      setSelectedRow(null);
      setRefresh(x => x + 1);
      showSnackbar('Message queue purged successfully!', 'success');
    } catch (err: any) {
      Logger.error('Settings/WorkerPool', 'Failed to purge message queue: ' + (err?.message || ''));
      showSnackbar(err?.response?.data?.message || err?.message || 'Failed to purge queue', 'error');
    } finally {
      setPurging(false);
    }
  };

  const toggleStatus = async (row: any) => {
    try {
      const next = row.status === 'active' ? 'inactive' : 'active';
      await apiClient.patch(`/iu/workerpool/${row._id}/status`, { status: next });
      setRefresh(x => x + 1);
    } catch (err: any) {
      Logger.error('Settings/WorkerPool', 'Failed to toggle status: ' + (err?.message || ''));
    }
  };

  const headerRightContent = (
    <Button 
      variant="contained" 
      color="secondary" 
      startIcon={<Add />} 
      onClick={() => { setForm({ _id: '', poolName: '', location: '', countryCode: '', poolType: 'GSM' }); setDialogOpen(true); }}
    >
      New Worker Pool
    </Button>
  );

  const handleSubmit = async () => {
    try {
      if (!form.poolName || !form.location || !form.countryCode || !form.poolType) return;
      setCreating(true);
      if (form._id) {
        await apiClient.put(`/iu/workerpool/${form._id}`, {
          poolName: form.poolName,
          location: form.location,
          countryCode: form.countryCode,
          poolType: form.poolType,
        });
        showSnackbar('Worker Pool updated successfully!', 'success');
      } else {
        await apiClient.post('/iu/workerpool', {
          poolName: form.poolName,
          location: form.location,
          countryCode: form.countryCode,
          poolType: form.poolType,
        });
        showSnackbar('Worker Pool created successfully!', 'success');
      }
      setDialogOpen(false);
      setForm({ _id: '', poolName: '', location: '', countryCode: '', poolType: 'GSM' });
      setRefresh(x => x + 1);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'An unexpected error occurred';
      showSnackbar(errorMessage, 'error');
      Logger.error('Settings/WorkerPool', 'Failed to create/update worker pool: ' + (err?.message || ''));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Worker Pool</Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, v) => v && setViewMode(v)}
          color="secondary"
          sx={{ mr: 1 }}
        >
          <ToggleButton value="table" aria-label="table-view">
            <TableRowsIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="tiles" aria-label="tile-view">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        {headerRightContent}
      </Box>

      {viewMode === 'table' ? (
        <DataTableWithStaticSchema
          dataUrl="/iu/workerpool"
          schemaObject={schemaObject as any}
          columnsToExclude={columnsToExclude}
          columnRenderers={columnRenderers}
          onEdit={onEdit}
          onDelete={onDelete}
          customActions={(row: any) => ([
            {
              label: 'Credentials',
              onClick: () => openCredentials(row),
              color: 'info',
              icon: <KeyIcon />
            }
          ])}
          refreshTrigger={refresh}
          columnsWithDisabledFilter={[...columnsWithDisabledFilter, 'status']}
        />
      ) : (
        <Box>
          {loadingTiles ? (
            <Typography variant="body2" color="textSecondary">Loading...</Typography>
          ) : pools.length === 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography align="center">No worker pools found.</Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {pools.map((row: any) => {
                const workersCount = Array.isArray(row.workers) ? row.workers.length : 0;
                const queueCount = queueCountOf(row);
                return (
                  <Grid key={row._id} item xs={12} sm={6} md={4} lg={3}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardHeader
                        titleTypographyProps={{ variant: 'subtitle1' }}
                        subheaderTypographyProps={{ variant: 'caption' }}
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="subtitle1" noWrap>{row.poolName || '-'}</Typography>
                            <Chip
                              label={String(row.status || '').toUpperCase()}
                              color={row.status === 'active' ? 'success' : 'warning'}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        }
                        subheader={`${row.location || '-'} · ${row.countryCode || ''}`}
                      />
                      <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Workers</Typography>
                            <Typography variant="body2">{workersCount}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Queue</Typography>
                            <Typography variant="body2">{queueCount}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Pool Type</Typography>
                            <Typography variant="body2">{row.poolType || '-'}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Status</Typography>
                            <Switch
                              checked={row.status === 'active'}
                              onChange={() => toggleStatus(row)}
                              color={row.status === 'active' ? 'success' : 'warning'}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" align="center" display="block">Manage Workers</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Workers">
                              <Button 
                                size="small" 
                                variant="contained" 
                                color="secondary" 
                                startIcon={<SimCardIcon fontSize="small" />} 
                                onClick={() => { window.location.hash = `/administration/worker-pool/${row._id}/workers`; }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Workers
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" align="center" display="block">Manage Queue</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Queue">
                              <Button 
                                size="small" 
                                variant="contained" 
                                color="secondary" 
                                startIcon={<VisibilityIcon fontSize="small" />} 
                                onClick={() => { window.location.hash = `/administration/worker-pool/${row._id}/messaging-queue`; }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Queue
                              </Button>
                            </Tooltip>
                            <Tooltip title="Purge Queue">
                              <Button 
                                size="small" 
                                color="error" 
                                variant="contained" 
                                startIcon={<CleaningServicesIcon fontSize="small" />} 
                                onClick={() => { setSelectedRow(row); setPurgeConfirmOpen(true); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Purge
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" align="center" display="block">Manage Pool</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title="Credentials">
                              <Button 
                                size="small" 
                                color="info" 
                                variant="contained" 
                                startIcon={<KeyIcon fontSize="small" />} 
                                onClick={() => openCredentials(row)}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Credentials
                              </Button>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <Button 
                                size="small" 
                                color="secondary" 
                                variant="contained" 
                                startIcon={<EditIcon fontSize="small" />} 
                                onClick={() => onEdit(row)}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <Button 
                                size="small" 
                                color="error" 
                                variant="contained" 
                                startIcon={<DeleteIcon fontSize="small" />} 
                                onClick={() => onDelete(row)}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Edit Worker Pool' : 'New Worker Pool'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Pool Name"
            value={form.poolName}
            onChange={(e) => setForm({ ...form, poolName: e.target.value })}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Country Code"
            value={form.countryCode}
            onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
            fullWidth
            required
            margin="normal"
            placeholder="e.g., US, UK, IN"
          />
          <TextField
            label="Pool Type"
            value={form.poolType}
            onChange={(e) => setForm({ ...form, poolType: e.target.value })}
            select
            fullWidth
            required
            margin="normal"
          >
            <MenuItem value="GSM">GSM</MenuItem>
            <MenuItem value="WhatsApp">WhatsApp</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined"
            color="secondary"
            onClick={() => setDialogOpen(false)}
            startIcon={<Cancel />}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            startIcon={<Save />}
            variant="contained"
            color="success"
            disabled={creating || !form.poolName || !form.location || !form.countryCode || !form.poolType}
          >
            {creating ? (form._id ? 'Updating...' : 'Creating...') : (form._id ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cannot Delete Dialog */}
      <Dialog open={cannotDeleteOpen} onClose={() => setCannotDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cannot Delete Worker Pool</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            This pool cannot be deleted because it has existing resources.
          </Typography>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2">Workers: {Array.isArray(selectedRow?.workers) ? selectedRow.workers.length : 0}</Typography>
            <Typography variant="body2">Messages in Queue: {queueCountOf(selectedRow)}</Typography>
          </Box>
          <Typography sx={{ pt: 2 }} color="text.secondary">
            Please remove all workers and purge the message queue before deleting this pool.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCannotDeleteOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            Once deleted, this Worker Pool and its workers will not be recoverable.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Purge Queue Confirm Dialog */}
      <Dialog open={purgeConfirmOpen} onClose={() => setPurgeConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Purge Message Queue</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            This action will delete all items from the queue and cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setPurgeConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmPurge} disabled={purging}>
            {purging ? 'Purging...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credDialogOpen} onClose={() => setCredDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>API Credentials</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Client ID</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace' }}>{selectedRow?.clientId || ''}</Typography>
            {selectedRow?.clientId && (
              <IconButton size="small" color="secondary" onClick={() => copyToClipboard(selectedRow.clientId)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Secret Key</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace' }}>{selectedRow?.secretKey ? '********' : ''}</Typography>
            {selectedRow?.secretKey && (
              <IconButton size="small" color="secondary" onClick={() => copyToClipboard(selectedRow.secretKey)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setCredDialogOpen(false)}>Close</Button>
          <Button variant="contained" color="warning" onClick={handleRefreshSecret} disabled={refreshingSecret}>{refreshingSecret ? 'Refreshing...' : 'Refresh Secret'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


